import { z } from "zod";

/** Année observée, pas une campagne réglementaire ni un droit de récolte. */
export const anneeReferenceVignobleSchema = z.number().int().min(1900).max(9999);
export const vignobleAnneeInputSchema = z
  .object({ annee_reference: anneeReferenceVignobleSchema.optional() })
  .optional();

export const stadeCulturalSchema = z.enum([
  "premiere_feuille",
  "deuxieme_feuille",
  "production",
  "jachere",
  "arrachee",
  "a_preciser",
  "plantation_prevue",
]);
export type StadeCultural = z.infer<typeof stadeCulturalSchema>;
export const STADE_CULTURAL_LABELS: Record<StadeCultural, string> = {
  premiere_feuille: "Première feuille",
  deuxieme_feuille: "Deuxième feuille",
  production: "En production",
  jachere: "Jachère",
  arrachee: "Arrachée",
  a_preciser: "À préciser",
  plantation_prevue: "Plantation prévue",
};

type StatutSaisi = "production" | "jeune_vigne" | "arrachee" | "jachere";
export interface ProjectionStadeCultural {
  stade_cultural: StadeCultural;
  annee_reference: number;
  origine_stade: "annee_plantation" | "statut_declare" | "information_manquante";
}

/**
 * Âge cultural : N = première feuille, N+1 = deuxième, N+2 = production.
 * Ne prouve ni la date complète de plantation ni le bénéfice AOC. Jachère et
 * arrachage déclarés restent visibles : aucun passé cultural n'est inventé.
 */
export function projeterStadeCultural(
  statut: StatutSaisi,
  anneePlantation: number | null,
  anneeReference: number,
): ProjectionStadeCultural {
  if (!Number.isInteger(anneeReference) || anneeReference < 1900 || anneeReference > 9999)
    throw new RangeError("Année de référence invalide.");
  if (statut === "jachere" || statut === "arrachee")
    return {
      stade_cultural: statut,
      annee_reference: anneeReference,
      origine_stade: "statut_declare",
    };
  if (anneePlantation === null || !Number.isInteger(anneePlantation))
    return {
      stade_cultural: "a_preciser",
      annee_reference: anneeReference,
      origine_stade: "information_manquante",
    };
  const age = anneeReference - anneePlantation;
  return {
    stade_cultural:
      age < 0
        ? "plantation_prevue"
        : age === 0
          ? "premiere_feuille"
          : age === 1
            ? "deuxieme_feuille"
            : "production",
    annee_reference: anneeReference,
    origine_stade: "annee_plantation",
  };
}
